import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import type { TransitTaskFindInterface } from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-find.dto';
import {
  TransitTaskFormDto,
  type TransitTaskFormInterface
} from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-form.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { CarCategoryService } from '@zaoblako/web/core/services/passage/car-category.service';
import { TransitTaskService } from '@zaoblako/web/core/services/passage/transit-task.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { CarCategorySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/car-category-single-select/car-category-single-select.component';
import { ExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-single-select/external-user-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { RoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-multi-select/room-multi-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { forkJoin, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'crm-transit-task-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SpinnerComponent,
    ButtonModule,
    JoinPipe,
    DatepickerComponent,
    InputTextComponent,
    DividerModule,
    ExternalUserSingleSelectComponent,
    MultiSelectComponent,
    UserMultiSelectComponent,
    UserSingleSelectComponent,
    CarCategorySingleSelectComponent,
    ClientAutoCompleteComponent,
    RoomMultiSelectComponent
  ],
  templateUrl: './transit-task-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TransitTaskFormComponent extends BaseFormComponent<TransitTaskFormInterface> implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public task = <TransitTaskFormInterface>{};
  public dto = TransitTaskFormDto;

  @ViewChild('externalUserSingleSelectComponent')
  public readonly externalUserSingleSelectComponent: ExternalUserSingleSelectComponent;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public engine: 'class-transformer' | 'serde' = 'serde';

  public users: UserFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly transitTaskService: TransitTaskService,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService,
    private readonly carCategoryService: CarCategoryService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([this.userService.findAll(), this.userGroupService.find<UserGroupFindInterface>()]).subscribe({
      next: ([users, userGroups]) => {
        this.users = users;
        this.userGroups = userGroups;
        this.cdRef.markForCheck();

        if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
          this.carCategoryService.findDefault().subscribe((defaultCategory) => {
            if (!this.task.category) {
              this.task.category = defaultCategory;
              this.cdRef.markForCheck();
            }
          });
        }
      }
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.transitTaskService
      .findById<TransitTaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          this.task = task;

          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTask() {
    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.transitTaskService.update<TransitTaskFormInterface, TransitTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Заявка №${task?.id} обновлена`);
            this.formStoreService.saveNext(task, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.transitTaskService.create<TransitTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/passage/transit-task/card/' + task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTask() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить заявку №${this.formStoreService.getState.forms[this.index]?.entityId}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.transitTaskService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(
            `Заявка №${this.formStoreService.getState.forms[this.index]?.entityId} удалена`
          );
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public selectedClient() {
    this.task.externalUser = null;
    this.cdRef.markForCheck();
  }

  public openFormCreateExternalUser() {
    const result = this.formStoreService.addForm(TypeFormEnum.EXTERNAL_USER_CREATE, {
      headerText: 'Добавление внешнего пользователя',
      widthSize: 'form-sidebar-small',
      data: {
        client: this.task?.client,
        contragents: this.task?.client?.contragents
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((externalUser: ExternalUserFindInterface) => {
        if (externalUser) {
          this.externalUserSingleSelectComponent?.loadAll();
          this.task.externalUser = externalUser;
          this.cdRef.markForCheck();
        }
      })
    );
  }
}
